import React, { useEffect, useMemo, useState } from 'react'
import { Hash, Clock, Save } from 'lucide-react'

import { supabase } from '../lib/supabase'
import { useAuth, useTeamAccess } from '../contexts/AuthContext'
import { Button } from './ui/button'
import { Input } from './ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select'

// ============================================================================
// Component: PodStandupOverrides
//
// Phase 1, item 6 of docs/ALERTING_PERMISSIONS_PLAN.md's build-first slice:
// "Managers set channel + time per pod. Team-level config remains the
// default; a pod without an override falls back to it silently." Mounted at
// the bottom of the Schedule tab (StandupConfiguration.tsx), below the
// team-wide config it's an override of.
//
// The standup-digest-scheduler resolves these overrides before posting. A
// project-specific channel/time takes precedence over the workspace default.
//
// PERMISSIONS: admins see all projects; managers see and edit only their
// assigned projects. Only a team admin, or a pod's own assigned manager, can
// edit that pod's row -- enforced both here (disabled inputs)
// and in Postgres (pod_standup_overrides_write RLS via
// private.can_write_pod_standup_override), so this component's gating is
// UX, not the security boundary.
// ============================================================================

interface PodStandupOverridesProps {
  teamId: string
}

interface Pod {
  id: string
  name: string
}

interface OverrideRow {
  pod_id: string
  digest_channel: string | null
  digest_time: string | null
}

interface SlackChannel {
  id: string
  name: string
  is_member: boolean
  is_private: boolean
}

interface TeamDefaults {
  digest_channel: string | null
  digest_time: string | null
}

const TEAM_DEFAULT_VALUE = '__team_default__'

export const PodStandupOverrides: React.FC<PodStandupOverridesProps> = ({ teamId }) => {
  const { role } = useTeamAccess(teamId)
  const { user } = useAuth()
  const isAdmin = role === 'admin' || role === 'owner'
  const isManager = role === 'manager'

  const [pods, setPods] = useState<Pod[]>([])
  const [managedPodIds, setManagedPodIds] = useState<Set<string>>(new Set())
  const [overrides, setOverrides] = useState<Record<string, OverrideRow>>({})
  const [teamDefaults, setTeamDefaults] = useState<TeamDefaults>({ digest_channel: null, digest_time: null })
  const [channels, setChannels] = useState<SlackChannel[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [savingPodId, setSavingPodId] = useState<string | null>(null)
  // Per-pod draft edits, keyed by pod id -- lets each row save independently.
  const [drafts, setDrafts] = useState<Record<string, { channel: string | null; time: string }>>({})

  useEffect(() => {
    let cancelled = false

    const load = async () => {
      setLoading(true)
      setError('')

      const [teamResult, podsResult] = await Promise.all([
        supabase.from('teams').select('standup_config').eq('id', teamId).single(),
        (supabase.from as any)('pods').select('id, name').eq('team_id', teamId),
      ])

      if (cancelled) return

      if (teamResult.error || podsResult.error) {
        console.error('Failed to load pod standup overrides:', teamResult.error ?? podsResult.error)
        setError('Could not load per-pod standup settings.')
        setLoading(false)
        return
      }

      const config = (teamResult.data?.standup_config as Record<string, unknown> | null) ?? {}
      setTeamDefaults({
        digest_channel: (config.digest_channel as string | null) ?? null,
        digest_time: (config.digest_time as string | null) ?? null,
      })

      const podRows = (podsResult.data ?? []) as Pod[]
      setPods(podRows)

      if (podRows.length === 0) {
        setOverrides({})
        setLoading(false)
        return
      }

      const { data: overrideRows, error: overrideError } = await (supabase.from as any)(
        'pod_standup_overrides'
      )
        .select('pod_id, digest_channel, digest_time')
        .in(
          'pod_id',
          podRows.map((p) => p.id)
        )

      if (cancelled) return

      if (overrideError) {
        console.error('Failed to load pod standup overrides:', overrideError)
        setError('Could not load per-pod standup settings.')
        setLoading(false)
        return
      }

      const byPodId: Record<string, OverrideRow> = {}
      for (const row of (overrideRows ?? []) as OverrideRow[]) {
        byPodId[row.pod_id] = row
      }
      setOverrides(byPodId)
      setDrafts(
        Object.fromEntries(
          podRows.map((p) => [
            p.id,
            {
              channel: byPodId[p.id]?.digest_channel ?? null,
              time: byPodId[p.id]?.digest_time ?? '',
            },
          ])
        )
      )
      setLoading(false)
    }

    load()
    return () => {
      cancelled = true
    }
  }, [teamId])

  // Managed pods, manager-only -- same query pattern as TeamAgents.tsx /
  // AgentDetail.tsx (pod_managers' own SELECT policy is permissive/true, not
  // team-scoped, so this filters to this team's pod ids itself).
  useEffect(() => {
    let cancelled = false

    const loadManagedPods = async () => {
      if (!isManager || !user?.id || pods.length === 0) {
        setManagedPodIds(new Set())
        return
      }
      const { data, error: pmError } = await (supabase.from as any)('pod_managers')
        .select('pod_id')
        .eq('auth_user_id', user.id)
        .in(
          'pod_id',
          pods.map((p) => p.id)
        )
      if (cancelled) return
      if (pmError) {
        console.error('Failed to load managed pods:', pmError)
        return
      }
      setManagedPodIds(new Set((data ?? []).map((r: { pod_id: string }) => r.pod_id)))
    }

    loadManagedPods()
    return () => {
      cancelled = true
    }
  }, [isManager, user?.id, pods])

  // Any pod this viewer can edit needs the channel list -- not admin-only
  // here, unlike StandupConfiguration.tsx's team-level section, since a
  // manager who isn't an admin still needs to pick a channel for their own
  // pod. slack-channels has no server-side role gate (checked).
  const canEditAnyPod = isAdmin || managedPodIds.size > 0
  useEffect(() => {
    if (!canEditAnyPod) return
    let cancelled = false
    ;(async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession()
        if (!session) return
        const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/slack-channels`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({ team_id: teamId }),
        })
        if (cancelled) return
        if (response.ok) {
          const data = await response.json()
          setChannels(data.channels || [])
        }
      } catch (err) {
        console.error('Failed to load Slack channels for pod overrides:', err)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [canEditAnyPod, teamId])

  const canEditPod = (podId: string) => isAdmin || managedPodIds.has(podId)

  const handleSave = async (podId: string) => {
    const draft = drafts[podId]
    if (!draft) return
    setSavingPodId(podId)
    setError('')

    const { error: saveError } = await (supabase.from as any)('pod_standup_overrides').upsert(
      {
        pod_id: podId,
        digest_channel: draft.channel,
        digest_time: draft.time || null,
        updated_by: user?.id ?? null,
      },
      { onConflict: 'pod_id' }
    )

    setSavingPodId(null)

    if (saveError) {
      console.error('Failed to save pod standup override:', saveError)
      setError(`Could not save the override for this team: ${saveError.message}`)
      return
    }

    setOverrides((prev) => ({
      ...prev,
      [podId]: { pod_id: podId, digest_channel: draft.channel, digest_time: draft.time || null },
    }))
  }

  const teamDefaultLabel = useMemo(() => {
    const channelPart = teamDefaults.digest_channel
      ? `#${channels.find((c) => c.id === teamDefaults.digest_channel)?.name ?? teamDefaults.digest_channel}`
      : 'no channel set'
    const timePart = teamDefaults.digest_time ?? 'no time set'
    return `${channelPart} at ${timePart}`
  }, [teamDefaults, channels])

  if (loading) return null
  if (pods.length === 0) return null

  return (
    <div className="border-t pt-4 mt-4">
      <div className="mb-2">
        <h4 className="text-sm font-medium text-foreground">Pod overrides</h4>
        <p className="text-xs text-muted-foreground">
          Give a pod its own daily digest, with its own channel and time (team default: {teamDefaultLabel}).
          It covers that pod's standups and its members' connector activity, and posts even if the team-wide
          digest is off. Pod members still appear in the team-wide digest too. A pod with no override
          gets no separate digest.
        </p>
      </div>

      {error && <p className="text-sm text-destructive mb-2">{error}</p>}

      {/* One divided list (row dividers via divide-y + border-y, per
          UI_STYLE_GUIDE.md §3), not a stack of individually-bordered boxes. */}
      <div className="divide-y divide-border border-y border-border">
        {pods.map((pod) => {
          const editable = canEditPod(pod.id)
          const draft = drafts[pod.id] ?? { channel: null, time: '' }
          const hasOverride = !!overrides[pod.id]?.digest_channel || !!overrides[pod.id]?.digest_time

          return (
            <div key={pod.id} className="p-3">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                <div className="min-w-0">
                  <div className="text-sm font-medium text-foreground">{pod.name}</div>
                  <div className="text-xs text-muted-foreground">
                    {hasOverride ? 'Using its own override' : 'Using the team default'}
                  </div>
                </div>

                {editable ? (
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                    <div className="flex items-center gap-1">
                      <Hash className="h-4 w-4 text-muted-foreground" />
                      <Select
                        value={draft.channel ?? TEAM_DEFAULT_VALUE}
                        onValueChange={(value) =>
                          setDrafts((prev) => ({
                            ...prev,
                            [pod.id]: { ...prev[pod.id], channel: value === TEAM_DEFAULT_VALUE ? null : value },
                          }))
                        }
                      >
                        <SelectTrigger className="w-full sm:w-48">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value={TEAM_DEFAULT_VALUE}>Team default</SelectItem>
                          {channels.map((channel) => (
                            <SelectItem key={channel.id} value={channel.id}>
                              #{channel.name}
                              {channel.is_private ? ' 🔒' : ''}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="flex items-center gap-1">
                      <Clock className="h-4 w-4 text-muted-foreground" />
                      <Input
                        type="time"
                        className="w-full sm:w-32"
                        value={draft.time}
                        placeholder="Team default"
                        onChange={(e) =>
                          setDrafts((prev) => ({ ...prev, [pod.id]: { ...prev[pod.id], time: e.target.value } }))
                        }
                      />
                    </div>

                    <Button
                      type="button"
                      size="sm"
                      disabled={savingPodId === pod.id}
                      onClick={() => handleSave(pod.id)}
                      className="gap-1"
                    >
                      <Save className="h-3.5 w-3.5" />
                      {savingPodId === pod.id ? 'Saving…' : 'Save'}
                    </Button>
                  </div>
                ) : (
                  <div className="text-xs text-muted-foreground">
                    {hasOverride
                      ? `#${channels.find((c) => c.id === overrides[pod.id]?.digest_channel)?.name ?? overrides[pod.id]?.digest_channel ?? 'channel'} at ${overrides[pod.id]?.digest_time ?? teamDefaults.digest_time ?? '—'}`
                      : 'Only this pod’s managers or a team admin can set an override.'}
                  </div>
                )}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
